import { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, AlertTriangle, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts";
import { useAuth } from "@/contexts/AuthContext";
import PrintPageButton from "@/components/PrintPageButton";
import { CashFlowScenario, SavingsGoalsSection } from "@/components/CashFlowExtras";

const emptyBal = () => ({
    bank_account_id: "",
    balance: 0,
    as_of_date: new Date().toISOString().slice(0, 10),
    remarks: "",
});

export default function CashFlow() {
    const { hasRole } = useAuth();
    const [window, setWindow] = useState(90);
    const [threshold, setThreshold] = useState(0);
    const [forecast, setForecast] = useState(null);
    const [balances, setBalances] = useState(null);
    const [banks, setBanks] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyBal());
    const [busy, setBusy] = useState(false);

    const load = () => {
        api.get("/cash-flow/forecast", { params: { window, threshold } })
            .then((r) => setForecast(r.data))
            .catch(() => setForecast(null));
        api.get("/cash-flow/bank-balance").then((r) => setBalances(r.data)).catch(() => setBalances(null));
        api.get("/bank-accounts").then((r) => setBanks(r.data || [])).catch(() => setBanks([]));
    };
    useEffect(() => { load(); }, [window, threshold]);

    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
    const setInput = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submitBalance = async (e) => {
        e?.preventDefault();
        if (!form.bank_account_id) { toast.error("Pick a bank account"); return; }
        setBusy(true);
        try {
            await api.post("/cash-flow/bank-balance", {
                ...form,
                balance: Number(form.balance || 0),
            });
            toast.success("Balance snapshot saved");
            setOpen(false);
            setForm(emptyBal());
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to save");
        } finally {
            setBusy(false);
        }
    };

    const chartData = useMemo(() => {
        if (!forecast?.series) return [];
        return forecast.series.map((s) => ({
            ...s,
            shortAbove: s.balance >= threshold ? s.balance : null,
            shortBelow: s.balance < threshold ? s.balance : null,
        }));
    }, [forecast, threshold]);

    return (
        <div data-testid="cashflow-page">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow">Cash Flow Forecast · Money projection</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Where will you land?
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        A projected balance built from your bank snapshots, recurring income,
                        upcoming card bills, and subscription renewals. Red bands = shortfall risk.
                    </p>
                </div>
                {hasRole("admin", "finance") && (
                    <div className="flex items-center gap-2">
                        <PrintPageButton />
                        <Button onClick={() => setOpen(true)} data-testid="cashflow-add-balance-btn" className="bg-stone-900 hover:bg-stone-800 text-white">
                            <Plus size={16} className="mr-1.5" /> Snapshot balance
                        </Button>
                    </div>
                )}
            </div>

            {/* Bank balance row */}
            {balances && balances.accounts?.length > 0 && (
                <div className="mb-6">
                    <div className="eyebrow mb-2">Latest bank balances</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {balances.accounts.map((a) => (
                            <div key={a.bank_account_id} className="kpi-card">
                                <div className="text-xs text-stone-500">{a.bank_name} · ••{a.last_4}</div>
                                <div className="font-display text-xl mt-1 tabular-nums">
                                    {a.balance != null ? fmtMoney(a.balance) : <span className="text-stone-400 text-sm">Not set</span>}
                                </div>
                                {a.as_of_date && <div className="text-[10.5px] text-stone-400 mt-0.5">as of {fmtDate(a.as_of_date)}</div>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Forecast KPIs */}
            {forecast && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="kpi-card">
                        <div className="eyebrow flex items-center gap-1.5"><Wallet size={11} /> Starting balance</div>
                        <div className="font-display text-2xl mt-2 tabular-nums">{fmtMoney(forecast.starting_balance)}</div>
                    </div>
                    <div className="kpi-card">
                        <div className="eyebrow flex items-center gap-1.5"><TrendingUp size={11} /> Projected inflows</div>
                        <div className="font-display text-2xl mt-2 tabular-nums text-emerald-700">+{fmtMoney(forecast.totals.inflows)}</div>
                    </div>
                    <div className="kpi-card">
                        <div className="eyebrow flex items-center gap-1.5"><TrendingDown size={11} /> Projected outflows</div>
                        <div className="font-display text-2xl mt-2 tabular-nums text-red-700">−{fmtMoney(forecast.totals.outflows)}</div>
                    </div>
                    <div className="kpi-card">
                        <div className="eyebrow flex items-center gap-1.5"><AlertTriangle size={11} /> Shortfall days</div>
                        <div className={`font-display text-2xl mt-2 tabular-nums ${forecast.shortfall_days > 0 ? "text-red-700" : "text-emerald-700"}`}>
                            {forecast.shortfall_days}
                        </div>
                        <div className="text-[11px] text-stone-500 mt-1">Min balance {fmtMoney(forecast.min_balance)}</div>
                    </div>
                </div>
            )}

            {/* Window + threshold controls */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="text-xs text-stone-500 uppercase tracking-widest">Window</div>
                {[30, 60, 90, 120, 180].map((w) => (
                    <button
                        key={w}
                        onClick={() => setWindow(w)}
                        data-testid={`cashflow-window-${w}`}
                        className={`px-3 py-1.5 text-xs rounded border transition-colors ${window === w ? "bg-stone-900 text-white border-stone-900" : "border-stone-200 hover:border-stone-400 text-stone-700"}`}
                    >
                        {w} days
                    </button>
                ))}
                <div className="ml-auto flex items-center gap-2">
                    <Label className="text-xs text-stone-500">Shortfall threshold ₹</Label>
                    <Input type="number" value={threshold} onChange={(e) => setThreshold(Number(e.target.value || 0))} className="w-28 h-8 text-sm" />
                </div>
            </div>

            {/* Chart */}
            <div className="bg-white border border-stone-200 rounded-lg p-4 mb-6">
                {chartData.length === 0 ? (
                    <div className="text-center text-stone-500 py-14">Loading forecast…</div>
                ) : (
                    <ResponsiveContainer width="100%" height={320}>
                        <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="ok" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#047857" stopOpacity={0.35} />
                                    <stop offset="100%" stopColor="#047857" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="short" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#b91c1c" stopOpacity={0.35} />
                                    <stop offset="100%" stopColor="#b91c1c" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                            <XAxis dataKey="date" tickFormatter={(v) => v?.slice(5)} fontSize={10} />
                            <YAxis tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} fontSize={10} />
                            <Tooltip
                                formatter={(v, k) => k === "balance" ? [fmtMoney(v), "Balance"] : [v, k]}
                                labelFormatter={(l) => fmtDate(l)}
                            />
                            <ReferenceLine y={threshold} stroke="#b91c1c" strokeDasharray="3 3" />
                            <Area type="monotone" dataKey="shortAbove" stroke="#047857" fill="url(#ok)" strokeWidth={2} />
                            <Area type="monotone" dataKey="shortBelow" stroke="#b91c1c" fill="url(#short)" strokeWidth={2} />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>

            {/* Upcoming events */}
            {forecast?.events?.length > 0 && (
                <div className="bg-white border border-stone-200 rounded-lg overflow-hidden mb-6">
                    <div className="px-4 py-3 border-b border-stone-100 eyebrow">Upcoming events in this window</div>
                    <table className="dense-table w-full">
                        <thead><tr><th>Date</th><th>Label</th><th>Type</th><th className="text-right">Amount</th></tr></thead>
                        <tbody>
                            {forecast.events.slice(0, 40).map((e, i) => (
                                <tr key={i}>
                                    <td className="tabular-nums">{fmtDate(e.date)}</td>
                                    <td>{e.label || "—"}</td>
                                    <td className="text-xs text-stone-500">{e.type}{e.sub_type ? ` · ${e.sub_type}` : ""}</td>
                                    <td className={`text-right tabular-nums font-medium ${e.amount >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                                        {e.amount >= 0 ? "+" : "−"}{fmtMoney(Math.abs(e.amount))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <CashFlowScenario window={window} />
            <SavingsGoalsSection />

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Snapshot bank balance</DialogTitle></DialogHeader>
                    <form onSubmit={submitBalance} className="space-y-4">
                        <div>
                            <Label className="eyebrow">Bank account</Label>
                            <Select value={form.bank_account_id} onValueChange={set("bank_account_id")}>
                                <SelectTrigger data-testid="balance-form-account"><SelectValue placeholder="Choose…" /></SelectTrigger>
                                <SelectContent>
                                    {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.bank_name} · ••{b.last_4} · {b.holder_name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="eyebrow">Balance (₹)</Label>
                            <Input type="number" value={form.balance} onChange={setInput("balance")} required data-testid="balance-form-amount" />
                        </div>
                        <div>
                            <Label className="eyebrow">As of date</Label>
                            <Input type="date" value={form.as_of_date} onChange={setInput("as_of_date")} required />
                        </div>
                        <div>
                            <Label className="eyebrow">Remarks</Label>
                            <Textarea rows={2} value={form.remarks} onChange={setInput("remarks")} />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={busy} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="balance-form-save">
                                {busy ? "Saving…" : "Save snapshot"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
